import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button, Input, Select, TextareaAutosize, FormTabs, FormTab, formatSelectOptions } from '../../../components'
import { userService } from '../../../services'
import { useMessage, useModalDialog } from '../../../hooks'
import { useClientCaseDialog } from '../hooks'
import { clientCaseService, clientService } from '../services'
import { slugify } from '../helpers'
import { appName } from '../appConfig'
import '../index.css'

const TABS = ['Kund', 'Sammanfattning', 'Utmaningar', 'Leveranser', 'Teknik', 'Resultat', 'Team']

const emptyProblem      = () => ({ id: crypto.randomUUID(), headline: '', headline_en: '', description: '', description_en: '', challenges: [] })
const emptyDeliverable  = () => ({ title: '', title_en: '', date: '', description: '', description_en: '', team: [], problem_id: null })
const emptyTechnology   = () => ({ category: '', category_en: '', tools: '' })
const emptyOutcome      = () => ({ metric: '', label: '', label_en: '' })
const emptyTeamMember   = () => ({ name: '', role: '', from: '', to: '', focus_area: '', contributions: [], contributions_en: [] })

// client identity is no longer stored in the JSONB — it's resolved server-side
// from client_case_client_id (see injectClientBlock in controllerClientCase.js).
const EMPTY_DATA = {
  engagement:  { start: '', end: '' },
  summary:     { headline: '', headline_en: '', description: '', description_en: '' },
  problems:    [],
  deliverables: [],
  technologies: [],
  outcomes:     [],
  team:         [],
}

// Which tab a validation key belongs to, so a failed publish can jump straight
// to the first tab with an error instead of leaving the user to hunt for it.
const tabForKey = (key) => {
  if (key.startsWith('deliverables.')) return 'Leveranser'
  if (key.startsWith('team.'))         return 'Team'
  return 'Kund'
}

const Field = ({ label, optional, required, children }) => (
  <div className="form-block vertical">
    <label>{label}{required && <span className="cc-required">*</span>}{optional && <span className="cc-optional">(valfritt)</span>}</label>
    {children}
  </div>
)

// Adapter for the shared Input onChange duality: a native event, or
// {name,value} from Input's own clear button.
const val = (e) => (e.target !== undefined ? e.target.value : e.value)

const TagInput = ({ values = [], onChange, placeholder }) => {
  const [input, setInput] = useState('')

  const add = (raw) => {
    const v = raw.trim()
    if (v && !values.includes(v)) onChange([...values, v])
    setInput('')
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(input) }
    if (e.key === 'Backspace' && input === '' && values.length) {
      onChange(values.slice(0, -1))
    }
  }

  return (
    <div className="cc-tag-input">
      {values.map((v, i) => (
        <span key={i} className="cc-tag">
          {v}
          <button className="cc-tag-remove" onClick={() => onChange(values.filter((_, idx) => idx !== i))}>×</button>
        </span>
      ))}
      <input
        className="cc-tag-text-input"
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => { if (input.trim()) add(input) }}
        placeholder={values.length === 0 ? placeholder : ''}
      />
    </div>
  )
}

const MONTHS = [
  ['01','Jan'],['02','Feb'],['03','Mar'],['04','Apr'],
  ['05','Maj'],['06','Jun'],['07','Jul'],['08','Aug'],
  ['09','Sep'],['10','Okt'],['11','Nov'],['12','Dec'],
]
const YEARS = Array.from({ length: 22 }, (_, i) => String(2014 + i))

const MonthPicker = ({ value, onChange, disabled, error }) => {
  const split = (v) => v && /^\d{4}-\d{2}$/.test(v) ? v.split('-') : ['', '']
  const [yr, setYr] = useState(() => split(value)[0])
  const [mo, setMo] = useState(() => split(value)[1])

  useEffect(() => {
    const [y, m] = split(value)
    setYr(y); setMo(m)
  }, [value])

  const handleYr = (y) => { setYr(y); if (y && mo) onChange(`${y}-${mo}`) }
  const handleMo = (m) => { setMo(m); if (yr && m) onChange(`${yr}-${m}`) }

  return (
    <div className="cc-month-picker">
      <select className={`cc-select${error ? ' cc-field-error' : ''}`} value={yr} onChange={e => handleYr(e.target.value)} disabled={disabled}>
        <option value="">År</option>
        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <select className={`cc-select${error ? ' cc-field-error' : ''}`} value={mo} onChange={e => handleMo(e.target.value)} disabled={disabled}>
        <option value="">Månad</option>
        {MONTHS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </select>
    </div>
  )
}

const ClientCaseEdit = () => {
  const { slug }   = useParams()
  const navigate   = useNavigate()
  const [searchParams] = useSearchParams()
  const isNew      = !slug

  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const { showDeleteClientCaseDialog } = useClientCaseDialog()

  const [activeTab, setActiveTab] = useState(TABS[0])
  const [uuid, setUuid]           = useState(null)
  const [formSlug, setFormSlug]   = useState('')
  const [lang, setLang]           = useState('sv')
  const [data, setData]           = useState(EMPTY_DATA)
  const [loading, setLoading]     = useState(!isNew)
  const [saving, setSaving]         = useState(false)
  const [generating, setGenerating] = useState(false)
  const [employees, setEmployees]     = useState([])
  const [clients, setClients]         = useState([])
  const [clientUuid, setClientUuid]   = useState('')
  const [isPublished, setIsPublished] = useState(false)
  const [invalidFields, setInvalidFields] = useState(new Set())
  const [previewing, setPreviewing]     = useState(false)
  const [draftHtml, setDraftHtml]       = useState(null)

  const selectedClient = clients.find(c => c.client_uuid === clientUuid)

  useEffect(() => {
    userService.getAll()
      .then(res => setEmployees((res.users || []).filter(u => !u.user_is_disabled).map(u => u.user_fullname).sort()))
      .catch(() => {})
  }, [])

  useEffect(() => {
    clientService.getAll()
      .then(res => setClients(res.clients || []))
      .catch(() => {})
  }, [])

  // Preselect via ?client=<slug> (e.g. "+ Nytt case" from a client's detail page).
  useEffect(() => {
    if (!isNew || clientUuid || !clients.length) return
    const preselect = searchParams.get('client')
    if (!preselect) return
    const match = clients.find(c => c.client_slug === preselect)
    if (match) setClientUuid(match.client_uuid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, isNew])

  useEffect(() => {
    if (!isNew) {
      clientCaseService.getBySlug(slug)
        .then(res => {
          const cc = res.clientCase
          setUuid(cc.client_case_uuid)
          setLang(cc.client_case_lang || 'sv')
          setClientUuid(cc.client_uuid || '')
          const raw = cc.client_case_data || EMPTY_DATA
          const rawProbs = Array.isArray(raw.problems) ? raw.problems : (raw.problem ? [raw.problem] : [])
          const normalised = { ...raw, problems: rawProbs.map(p => p.id ? p : { ...p, id: crypto.randomUUID() }) }
          setData(normalised)
          setFormSlug(cc.client_case_slug)
          setIsPublished(!!cc.client_case_generated_html)
          setLoading(false)
        })
        .catch(() => { showMessage('Kunde inte ladda', 'error'); setLoading(false) })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isNew])

  useEffect(() => {
    if (!isNew) return
    const name  = selectedClient?.company_name
    const start = data.engagement.start
    if (!name) { setFormSlug(''); return }
    const base = slugify(name)
    setFormSlug(start ? `${base}-${start}` : base)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, data.engagement.start, isNew])

  useEffect(() => {
    if (!draftHtml) return
    const onKey = (e) => { if (e.key === 'Escape') setDraftHtml(null) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [draftHtml])

  const clearInvalid = (key) => setInvalidFields(prev => { const n = new Set(prev); n.delete(key); return n })

  const set = (path, value) => {
    if (value) clearInvalid(path)
    setData(prev => {
      const next = { ...prev }
      const keys = path.split('.')
      let cur = next
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...cur[keys[i]] }
        cur = cur[keys[i]]
      }
      cur[keys[keys.length - 1]] = value
      return next
    })
  }

  const setArr = (key, index, field, value) => {
    if (value) clearInvalid(`${key}.${index}.${field}`)
    setData(prev => {
      const arr = [...(prev[key] || [])]
      arr[index] = { ...arr[index], [field]: value }
      return { ...prev, [key]: arr }
    })
  }

  const addItem = (key, emptyFn) => setData(prev => ({ ...prev, [key]: [...(prev[key] || []), emptyFn()] }))
  const removeItem = (key, index) => setData(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== index) }))

  const validate = () => {
    const errs = new Set()
    if (!clientUuid)               errs.add('client.uuid')
    if (!data.engagement.start)    errs.add('engagement.start')
    if (!data.summary.headline)    errs.add('summary.headline')
    if (!data.summary.description) errs.add('summary.description')
    ;(data.deliverables || []).forEach((d, i) => {
      if (!d.title)       errs.add(`deliverables.${i}.title`)
      if (!d.date)        errs.add(`deliverables.${i}.date`)
      if (!d.description) errs.add(`deliverables.${i}.description`)
    })
    ;(data.team || []).forEach((m, i) => {
      if (!m.name) errs.add(`team.${i}.name`)
      if (!m.role) errs.add(`team.${i}.role`)
      if (!m.from) errs.add(`team.${i}.from`)
    })
    return errs
  }

  const countInvalid = (...prefixes) => [...invalidFields].filter(k => prefixes.some(p => k === p || k.startsWith(`${p}.`))).length

  // An empty end date (start given, no end/"pågående" chosen) defaults to "pågående"
  // on save — an unfinished engagement is the far more common case than one that's
  // simply missing its end date by omission.
  const normalizedData = () => ({
    ...data,
    engagement: { ...data.engagement, end: data.engagement.end || 'ongoing' },
    team: (data.team || []).map(m => ({ ...m, to: m.to || 'ongoing' })),
  })

  const payload = () => ({ slug: formSlug, lang, data: normalizedData(), client_uuid: clientUuid || undefined })

  const handleSave = async () => {
    if (!clientUuid) {
      setInvalidFields(prev => new Set(prev).add('client.uuid'))
      setActiveTab('Kund')
      showMessage('Kund är obligatoriskt', 'error')
      return
    }
    setSaving(true)
    try {
      const normalized = normalizedData()
      setData(normalized)
      if (isNew) {
        const res = await clientCaseService.create(payload())
        showMessage('Skapad!')
        navigate(`/${appName}/cases/${res.clientCase.client_case_slug}/edit`)
      } else {
        await clientCaseService.update(uuid, payload())
        showMessage('Sparad!')
        if (formSlug !== slug) navigate(`/${appName}/cases/${formSlug}/edit`, { replace: true })
      }
    } catch (e) {
      showMessage(e.message || 'Kunde inte spara', 'error')
    }
    setSaving(false)
  }

  const handlePreviewDraft = async () => {
    setPreviewing(true)
    try {
      const res = await clientCaseService.previewDraft(payload())
      setDraftHtml(res.html)
    } catch (e) {
      showMessage('Förhandsvisning misslyckades — kontrollera att alla obligatoriska fält är ifyllda', 'error')
    }
    setPreviewing(false)
  }

  const handlePublish = async () => {
    const errs = validate()
    if (errs.size > 0) {
      setInvalidFields(errs)
      showMessage('Fyll i alla obligatoriska fält', 'error')
      setActiveTab(tabForKey([...errs][0]))
      return
    }
    setGenerating(true)
    try {
      const normalized = normalizedData()
      setData(normalized)
      let targetUuid = uuid
      if (isNew) {
        const res = await clientCaseService.create(payload())
        targetUuid = res.clientCase.client_case_uuid
      } else {
        await clientCaseService.update(uuid, payload())
      }
      await clientCaseService.generate(targetUuid)
      setIsPublished(true)
      showMessage('Publicerad!')
      if (isNew) navigate(`/${appName}/cases/${formSlug}/edit`)
    } catch (e) {
      showMessage(e.message || 'Kunde inte publicera', 'error')
    }
    setGenerating(false)
  }

  const deleteCase = () => {
    showDeleteClientCaseDialog({ client_name: selectedClient?.company_name, slug: formSlug }, async () => {
      await clientCaseService.remove(uuid)
        .then(() => { hideModalDialog(); showMessage('Borttagen'); navigate(`/${appName}/cases`) })
        .catch(err => showMessage(err.message || 'Kunde inte ta bort', 'error'))
    })
  }

  if (loading) return <div className="cc-page"><p style={{ color: 'var(--color-dark-grey)' }}>Laddar…</p></div>

  const renderClient = () => {
    const isOngoing = data.engagement.end === 'ongoing'
    return (
      <div className="cc-section">
        <div className="cc-section-title">Kund</div>
        <Field label="Kund" required>
          <Select
            name="client_uuid"
            value={clientUuid}
            options={formatSelectOptions({ options: clients, optionValue: 'client_uuid', optionLabel: 'company_name' })}
            onChange={({ option }) => setClientUuid(option ? option.value : '')}
            isNullable
            placeholder="Välj kund…"
            validation={invalidFields.has('client.uuid') ? { valid: false, message: 'Kund är obligatoriskt' } : undefined}
          />
        </Field>
        <Field label="URL-slug">
          <div className="cc-slug-display">{formSlug || <span className="cc-slug-placeholder">Genereras automatiskt från kund + startdatum</span>}</div>
        </Field>
        <div className="cc-row">
          <Field label="Bransch (SV)">
            <div className="cc-slug-display">{selectedClient?.company_industry || '—'}</div>
          </Field>
          <Field label="Bransch (EN)" optional>
            <div className="cc-slug-display">{selectedClient?.company_industry_en || '—'}</div>
          </Field>
        </div>
        <Field label="Språk för utdata">
          <Select
            name="lang"
            value={lang}
            options={formatSelectOptions({
              options: [
                { value: 'sv',   label: 'Svenska (sv)' },
                { value: 'en',   label: 'Engelska (en)' },
                { value: 'both', label: 'Tvåspråkig växling (both)' },
              ],
              optionValue: 'value', optionLabel: 'label',
            })}
            onChange={({ option }) => setLang(option ? option.value : 'sv')}
          />
        </Field>
        <div className="cc-section-title" style={{ marginTop: 24 }}>Uppdragsperiod</div>
        <div className="cc-row cc-row-tight">
          <Field label="Från" required>
            <MonthPicker value={data.engagement.start} onChange={v => set('engagement.start', v)} error={invalidFields.has('engagement.start')} />
          </Field>
          <Field label="Till">
            <div className="cc-ongoing-wrapper">
              {!isOngoing && <MonthPicker value={data.engagement.end} onChange={v => set('engagement.end', v)} />}
              <label className="cc-ongoing-label">
                <input type="checkbox" checked={isOngoing} onChange={e => set('engagement.end', e.target.checked ? 'ongoing' : '')} />
                Pågående
              </label>
            </div>
          </Field>
        </div>
      </div>
    )
  }

  const renderSummary = () => (
    <div className="cc-section">
      <div className="cc-section-title">Sammanfattning</div>
      <Field label="Rubrik (SV)" required>
        <Input type="text" name="summary_headline" value={data.summary.headline} onChange={e => set('summary.headline', val(e))}
          validation={invalidFields.has('summary.headline') ? { valid: false, message: 'Rubrik är obligatoriskt' } : undefined} />
      </Field>
      <Field label="Rubrik (EN)" optional>
        <Input type="text" name="summary_headline_en" value={data.summary.headline_en} onChange={e => set('summary.headline_en', val(e))} />
      </Field>
      <Field label="Beskrivning (SV)" required>
        <TextareaAutosize name="summary_description" value={data.summary.description} onChange={e => set('summary.description', e.target.value)} minRows={6}
          invalid={invalidFields.has('summary.description') ? 'Beskrivning är obligatoriskt' : undefined} />
      </Field>
      <Field label="Beskrivning (EN)" optional>
        <TextareaAutosize name="summary_description_en" value={data.summary.description_en} onChange={e => set('summary.description_en', e.target.value)} minRows={6} />
      </Field>
    </div>
  )

  const renderProblem = () => (
    <div className="cc-section">
      <div className="cc-section-title">Utmaningar</div>
      {(data.problems || []).map((p, i) => (
        <div className="cc-array-item" key={p.id || i}>
          <div className="cc-array-item-header">
            <span className="cc-array-item-label">Utmaning {i + 1}</span>
            <button className="cc-remove-btn" onClick={() => removeItem('problems', i)}>Ta bort</button>
          </div>
          <div className="cc-row">
            <Field label="Rubrik (SV)">
              <Input type="text" name={`problem_${i}_headline`} value={p.headline} onChange={e => setArr('problems', i, 'headline', val(e))} />
            </Field>
            <Field label="Rubrik (EN)" optional>
              <Input type="text" name={`problem_${i}_headline_en`} value={p.headline_en} onChange={e => setArr('problems', i, 'headline_en', val(e))} />
            </Field>
          </div>
          <Field label="Beskrivning (SV)">
            <TextareaAutosize name={`problem_${i}_description`} value={p.description} onChange={e => setArr('problems', i, 'description', e.target.value)} minRows={4} />
          </Field>
          <Field label="Beskrivning (EN)" optional>
            <TextareaAutosize name={`problem_${i}_description_en`} value={p.description_en} onChange={e => setArr('problems', i, 'description_en', e.target.value)} minRows={4} />
          </Field>
          <div className="cc-section-title" style={{ marginTop: 16 }}>Nyckelpunkter</div>
          {(p.challenges || []).map((c, ci) => (
            <div className="cc-array-item" key={ci}>
              <div className="cc-array-item-header">
                <span className="cc-array-item-label">Nyckelpunkt {ci + 1}</span>
                <button className="cc-remove-btn" onClick={() => {
                  const updated = [...data.problems]
                  updated[i] = { ...updated[i], challenges: updated[i].challenges.filter((_, idx) => idx !== ci) }
                  set('problems', updated)
                }}>Ta bort</button>
              </div>
              <div className="cc-row">
                <Field label="Text (SV)">
                  <Input type="text" name={`problem_${i}_challenge_${ci}_text`} value={c.text} onChange={e => {
                    const updated = [...data.problems]
                    const cArr = [...updated[i].challenges]
                    cArr[ci] = { ...cArr[ci], text: val(e) }
                    updated[i] = { ...updated[i], challenges: cArr }
                    set('problems', updated)
                  }} />
                </Field>
                <Field label="Text (EN)" optional>
                  <Input type="text" name={`problem_${i}_challenge_${ci}_text_en`} value={c.text_en} onChange={e => {
                    const updated = [...data.problems]
                    const cArr = [...updated[i].challenges]
                    cArr[ci] = { ...cArr[ci], text_en: val(e) }
                    updated[i] = { ...updated[i], challenges: cArr }
                    set('problems', updated)
                  }} />
                </Field>
              </div>
            </div>
          ))}
          <button className="cc-add-btn" onClick={() => {
            const updated = [...data.problems]
            updated[i] = { ...updated[i], challenges: [...(updated[i].challenges || []), { text: '', text_en: '' }] }
            set('problems', updated)
          }}>+ Lägg till nyckelpunkt</button>
        </div>
      ))}
      <button className="cc-add-btn" onClick={() => addItem('problems', emptyProblem)}>+ Lägg till utmaning</button>
    </div>
  )

  const renderDeliverables = () => (
    <div className="cc-section">
      <div className="cc-section-title">Leveranser</div>
      {data.deliverables.map((d, i) => (
        <div className="cc-array-item" key={i}>
          <div className="cc-array-item-header">
            <span className="cc-array-item-label">Leverans {i + 1}</span>
            <button className="cc-remove-btn" onClick={() => removeItem('deliverables', i)}>Ta bort</button>
          </div>
          <div className="cc-row">
            <Field label="Titel (SV)" required>
              <Input type="text" name={`deliverable_${i}_title`} value={d.title} onChange={e => setArr('deliverables', i, 'title', val(e))}
                validation={invalidFields.has(`deliverables.${i}.title`) ? { valid: false, message: 'Titel är obligatoriskt' } : undefined} />
            </Field>
            <Field label="Titel (EN)" optional>
              <Input type="text" name={`deliverable_${i}_title_en`} value={d.title_en} onChange={e => setArr('deliverables', i, 'title_en', val(e))} />
            </Field>
          </div>
          <Field label="Datum" required>
            <MonthPicker value={d.date} onChange={v => setArr('deliverables', i, 'date', v)} error={invalidFields.has(`deliverables.${i}.date`)} />
          </Field>
          <Field label="Beskrivning (SV)" required>
            <TextareaAutosize name={`deliverable_${i}_description`} value={d.description} onChange={e => setArr('deliverables', i, 'description', e.target.value)} minRows={3}
              invalid={invalidFields.has(`deliverables.${i}.description`) ? 'Beskrivning är obligatoriskt' : undefined} />
          </Field>
          <Field label="Beskrivning (EN)" optional>
            <TextareaAutosize name={`deliverable_${i}_description_en`} value={d.description_en} onChange={e => setArr('deliverables', i, 'description_en', e.target.value)} minRows={3} />
          </Field>
          <Field label="Teammedlemmar" optional>
            <div className="cc-tag-select">
              {(d.team || []).map(name => (
                <span key={name} className="cc-tag">
                  {name}
                  <button
                    className="cc-tag-remove"
                    onClick={() => setArr('deliverables', i, 'team', d.team.filter(n => n !== name))}
                  >×</button>
                </span>
              ))}
              <select
                className="cc-select cc-tag-add"
                value=""
                onChange={e => {
                  if (e.target.value) setArr('deliverables', i, 'team', [...(d.team || []), e.target.value])
                }}
              >
                <option value="">+ Lägg till medlem…</option>
                {employees.filter(n => !(d.team || []).includes(n)).map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
          </Field>
          <Field label="Utmaning" optional>
            {(data.problems || []).length === 0 ? (
              <div style={{ fontSize: '0.82em', color: 'var(--color-dark-grey)', fontStyle: 'italic', padding: '11px 0' }}>
                Inga utmaningar definierade ännu
              </div>
            ) : (
              <select
                className="cc-select"
                value={d.problem_id || ''}
                onChange={e => setArr('deliverables', i, 'problem_id', e.target.value || null)}
              >
                <option value="">Ingen</option>
                {(data.problems || []).map((p, pi) => (
                  <option key={p.id} value={p.id}>{p.headline || `Utmaning ${pi + 1}`}</option>
                ))}
              </select>
            )}
          </Field>
        </div>
      ))}
      <button className="cc-add-btn" onClick={() => addItem('deliverables', emptyDeliverable)}>+ Lägg till leverans</button>
    </div>
  )

  const renderTechnologies = () => (
    <div className="cc-section">
      <div className="cc-section-title">Teknik</div>
      {data.technologies.map((t, i) => (
        <div className="cc-array-item" key={i}>
          <div className="cc-array-item-header">
            <span className="cc-array-item-label">Kategori {i + 1}</span>
            <button className="cc-remove-btn" onClick={() => removeItem('technologies', i)}>Ta bort</button>
          </div>
          <div className="cc-row">
            <Field label="Kategori (SV)">
              <Input type="text" name={`technology_${i}_category`} value={t.category} onChange={e => setArr('technologies', i, 'category', val(e))} placeholder="Data Platform" />
            </Field>
            <Field label="Kategori (EN)" optional>
              <Input type="text" name={`technology_${i}_category_en`} value={t.category_en} onChange={e => setArr('technologies', i, 'category_en', val(e))} />
            </Field>
          </div>
          <Field label="Verktyg">
            <TagInput
              values={Array.isArray(t.tools) ? t.tools : []}
              onChange={v => setArr('technologies', i, 'tools', v)}
              placeholder="Skriv ett verktyg och tryck Enter…"
            />
          </Field>
        </div>
      ))}
      <button className="cc-add-btn" onClick={() => addItem('technologies', emptyTechnology)}>+ Lägg till teknikkategori</button>
    </div>
  )

  const renderOutcomes = () => (
    <div className="cc-section">
      <div className="cc-section-title">Resultat / Nyckeltal</div>
      {data.outcomes.map((o, i) => (
        <div className="cc-array-item" key={i}>
          <div className="cc-array-item-header">
            <span className="cc-array-item-label">Resultat {i + 1}</span>
            <button className="cc-remove-btn" onClick={() => removeItem('outcomes', i)}>Ta bort</button>
          </div>
          <div className="cc-row">
            <Field label="Värde">
              <Input type="text" name={`outcome_${i}_metric`} value={o.metric} onChange={e => setArr('outcomes', i, 'metric', val(e))} placeholder="50% eller 3x eller 12" />
            </Field>
            <Field label="Etikett (SV)">
              <Input type="text" name={`outcome_${i}_label`} value={o.label} onChange={e => setArr('outcomes', i, 'label', val(e))} />
            </Field>
            <Field label="Etikett (EN)" optional>
              <Input type="text" name={`outcome_${i}_label_en`} value={o.label_en} onChange={e => setArr('outcomes', i, 'label_en', val(e))} />
            </Field>
          </div>
        </div>
      ))}
      <button className="cc-add-btn" onClick={() => addItem('outcomes', emptyOutcome)}>+ Lägg till resultat</button>
    </div>
  )

  const renderTeam = () => (
    <div className="cc-section">
      <div className="cc-section-title">Kayenta Team</div>
      {data.team.map((m, i) => (
        <div className="cc-array-item" key={i}>
          <div className="cc-array-item-header">
            <span className="cc-array-item-label">{m.name || `Medlem ${i + 1}`}</span>
            <button className="cc-remove-btn" onClick={() => removeItem('team', i)}>Ta bort</button>
          </div>
          <div className="cc-row">
            <Field label="Namn" required>
              <select
                className={`cc-select${invalidFields.has(`team.${i}.name`) ? ' cc-field-error' : ''}`}
                value={m.name || ''}
                onChange={e => setArr('team', i, 'name', e.target.value)}
              >
                <option value="">Välj anställd…</option>
                {employees.map(name => <option key={name} value={name}>{name}</option>)}
                {m.name && !employees.includes(m.name) && (
                  <option value={m.name}>{m.name}</option>
                )}
              </select>
            </Field>
            <Field label="Roll" required>
              <Input type="text" name={`team_${i}_role`} value={m.role} onChange={e => setArr('team', i, 'role', val(e))} placeholder="Data Engineer"
                validation={invalidFields.has(`team.${i}.role`) ? { valid: false, message: 'Roll är obligatoriskt' } : undefined} />
            </Field>
          </div>
          <div className="cc-row cc-row-tight">
            <Field label="Från" required>
              <MonthPicker value={m.from} onChange={v => setArr('team', i, 'from', v)} error={invalidFields.has(`team.${i}.from`)} />
            </Field>
            <Field label="Till" optional>
              <div className="cc-ongoing-wrapper">
                {m.to !== 'ongoing' && <MonthPicker value={m.to} onChange={v => setArr('team', i, 'to', v)} />}
                <label className="cc-ongoing-label">
                  <input type="checkbox" checked={m.to === 'ongoing'} onChange={e => setArr('team', i, 'to', e.target.checked ? 'ongoing' : '')} />
                  Pågående
                </label>
              </div>
            </Field>
          </div>
          <Field label="Fokusområde" optional>
            <Input type="text" name={`team_${i}_focus_area`} value={m.focus_area} onChange={e => setArr('team', i, 'focus_area', val(e))} placeholder="Kort specialitet" />
          </Field>
          <Field label="Bidrag (SV)" optional>
            <TagInput
              values={Array.isArray(m.contributions) ? m.contributions : []}
              onChange={v => setArr('team', i, 'contributions', v)}
              placeholder="Skriv ett bidrag och tryck Enter…"
            />
          </Field>
          <Field label="Bidrag (EN)" optional>
            <TagInput
              values={Array.isArray(m.contributions_en) ? m.contributions_en : []}
              onChange={v => setArr('team', i, 'contributions_en', v)}
              placeholder="Skriv ett bidrag och tryck Enter…"
            />
          </Field>
        </div>
      ))}
      <button className="cc-add-btn" onClick={() => addItem('team', emptyTeamMember)}>+ Lägg till teammedlem</button>
    </div>
  )

  return (
    <div className="cc-page">
      <div className="cc-page-header">
        <div>
          <h1>{isNew ? 'Nytt kundcase' : (selectedClient?.company_name || formSlug)}</h1>
          {!isNew && selectedClient && (
            <div style={{ marginTop: 8 }}>
              <Button
                value="Utvärderingar →"
                className="button"
                onClick={() => navigate(`/${appName}/${selectedClient.client_slug}/reviews`)}
              />
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button value={previewing ? 'Laddar…' : 'Förhandsvisa ↗'} className="button" onClick={handlePreviewDraft} disabled={saving || generating || previewing} />
            {!isNew && (
              <Button
                value={generating ? 'Publicerar…' : isPublished ? 'Publicera om' : 'Publicera'}
                className={isPublished ? 'button' : 'button main-button'}
                onClick={handlePublish}
                disabled={saving || generating}
              />
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {!isNew && isPublished && (
              <Button value="Visa publicerad" className="button" onClick={() => navigate(`/${appName}/cases/${formSlug}`)} />
            )}
            {!isNew && (
              <Button value="Ta bort" className="button cc-danger-button" onClick={deleteCase} />
            )}
          </div>
        </div>
      </div>

      <div className="cc-form-wrap">
        <FormTabs activeTab={activeTab} onTabChange={setActiveTab}>
          <FormTab label={TABS[0]} badge={countInvalid('client.uuid', 'engagement.start')}>{renderClient()}</FormTab>
          <FormTab label={TABS[1]} badge={countInvalid('summary.headline', 'summary.description')}>{renderSummary()}</FormTab>
          <FormTab label={TABS[2]}>{renderProblem()}</FormTab>
          <FormTab label={TABS[3]} badge={countInvalid('deliverables')}>{renderDeliverables()}</FormTab>
          <FormTab label={TABS[4]}>{renderTechnologies()}</FormTab>
          <FormTab label={TABS[5]}>{renderOutcomes()}</FormTab>
          <FormTab label={TABS[6]} badge={countInvalid('team')}>{renderTeam()}</FormTab>
        </FormTabs>

        <div className="cc-form-footer">
          <Button value={saving ? 'Sparar…' : 'Spara'} className="button" onClick={handleSave} disabled={saving || generating} />
        </div>
      </div>

      {draftHtml && (
        <div className="cc-draft-overlay" onClick={() => setDraftHtml(null)}>
          <div className="cc-draft-panel" onClick={e => e.stopPropagation()}>
            <div className="cc-draft-bar">
              <span>Förhandsvisning (utkast)</span>
              <Button value="Stäng ✕" className="button" onClick={() => setDraftHtml(null)} />
            </div>
            <iframe className="cc-draft-frame" srcDoc={draftHtml} title="Förhandsvisning" />
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientCaseEdit
